import { React, PropTypes } from 'imports';
import ReactDOM from 'react-dom';
import { toArray } from 'utils';
import './Portal.less';

// A componentish and DOM-unaware implementation of https://reactjs.org/docs/portals.html
// Moves DOM of Portal.In into related Portal.Out component. Connection is established by unique 'portalKey' prop.
// There might be only one Portal.Out with a specific key in React tree, but as many Portal.In:s as needed. Their contents will be concatenated under Portal.Out.
// If there is no corresponding Portal.Out in the tree, content will be rendered right inside Portal.In.
// Portalling should follow all the rules described in aforementioned article, but I don't want to check them right now. If something doesn't follow, feel free to fix it. 
// TODO really use portals after upgrade to React 16

const ins = [];
const outs = {};

class PassContext extends React.PureComponent {
    static get propTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    static get childContextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    getChildContext() {
        const { children, ...context } = this.props;
        return context;
    }

    render() {
        return this.props.children;
    }
}

export class In extends React.PureComponent {
    static get displayName() {
        return 'Portal.In';
    }

    static get propTypes() {
        return {
            portalKey: PropTypes.string.isRequired,
            className: PropTypes.string,
            onConnect: PropTypes.func,
            onDisconnect: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            className: ''
        }
    }

    static get contextTypes() {
        return {
            intl: PropTypes.object,
            router: PropTypes.object,
            store: PropTypes.object
        }
    }

    constructor() {
        super();
        this._teleportableNodes = [];
    }

    _reconnect(prevProps, nextProps) {
        // TODO optimize

        let nodesChanged = false;
        const newTeleportableNodes = toArray(nextProps && nextProps.children || [])
            .flatten()
            .fullJoin(
                this._teleportableNodes,
                (_, index) => index,
                (_, index) => index,
                (child, node) => {
                    if (child && !node) {
                        node = document.createElement('div');
                        node.className = 'teleportable';
                        nodesChanged = true;
                    }
                    if (child && node) {
                        ReactDOM.render(<PassContext {...this.context}>{child}</PassContext>, node);
                    }
                    if (!child && node) {
                        ReactDOM.unmountComponentAtNode(node);
                        node = null;
                        nodesChanged = true;
                    }
                    return node;
                });

        if (newTeleportableNodes.some(v => v)) {
            if (!ins.includes(this)) {
                ins.push(this);
            }
        }
        else {
            if (ins.includes(this)) {
                ins.splice(ins.indexOf(this), 1);
            }
        }

        const
            prevOut = prevProps && prevProps.portalKey && outs[prevProps.portalKey],
            prevTargetNode = (prevOut || this).refs.root,
            nextOut = nextProps && nextProps.portalKey && outs[nextProps.portalKey],
            nextTargetNode = (nextOut || this).refs.root;

        if (prevTargetNode !== nextTargetNode) {
            if (prevOut) {
                prevOut.tryFireDisconnect(this);
            }

            if (prevOut && (nextProps || prevProps).onDisconnect) {
                (nextProps || prevProps).onDisconnect(this, nextOut);
            }
        }

        this._teleportableNodes.fullJoin(
            newTeleportableNodes,
            (_, index) => index,
            (_, index) => index,
            (oldNode, newNode) => {
                if (oldNode && (
                    !newNode ||
                    oldNode !== newNode ||
                    oldNode.parentNode !== nextTargetNode ||
                    nodesChanged
                )) {
                    oldNode.parentNode.removeChild(oldNode);
                }
            }
        );
        const refTarget = nextTargetNode.firstChild;
        this._teleportableNodes.fullJoin(
            newTeleportableNodes,
            (_, index) => index,
            (_, index) => index,
            (oldNode, newNode) => {
                if (newNode && (!oldNode || oldNode !== newNode || oldNode.parentNode !== nextTargetNode)) {
                    if (refTarget) {
                        nextTargetNode.insertBefore(newNode, refTarget);
                    }
                    else {
                        nextTargetNode.appendChild(newNode);
                    }
                }
            }
        );
        this._teleportableNodes = newTeleportableNodes;

        if (prevTargetNode !== nextTargetNode) {
            if (nextOut && (nextProps || prevProps).onConnect) {
                (nextProps || prevProps).onConnect(this, nextOut);
            }

            if (nextOut) {
                nextOut.tryFireConnect(this);
            }
        }
    }

    reconnect() {
        this._reconnect(this.props, this.props);
    }

    componentDidMount() {
        this._reconnect(null, this.props);
    }

    componentDidUpdate(prevProps) {
        this._reconnect(prevProps, this.props);
    }

    componentWillUnmount() {
        this._reconnect(this.props, null);
    }

    render() {
        return <div ref="root" className={`portal in ${this.props.className}`} />;
    }
}

export class Out extends React.PureComponent {
    static get displayName() {
        return 'Portal.Out';
    }

    static get propTypes() {
        return {
            portalKey: PropTypes.string.isRequired,
            className: PropTypes.string,
            onConnect: PropTypes.func,
            onDisconnect: PropTypes.func
        };
    }

    static get defaultProps() {
        return {
            className: ''
        }
    }

    _isReconnecting = false;
    _disconnectedItems = [];
    _connectedItems = [];

    tryFireDisconnect(sender) {
        if (!this._isRedisconnecting) {
            if (this.props.onDisconnect) {
                this.props.onDisconnect(this, [sender]);
            }
        }
        else {
            this._disconnectedItems.push(sender);
        }
    }

    tryFireConnect(sender) {
        if (!this._isReconnecting) {
            if (this.props.onConnect) {
                this.props.onConnect(this, [sender]);
            }
        }
        else {
            this._connectedItems.push(sender);
        }
    }

    _reconnect(prevProps, nextProps) {
        this._isReconnecting = true;

        const
            prevKey = prevProps && prevProps.portalKey,
            nextKey = nextProps && nextProps.portalKey;
        if (prevKey !== nextKey) {
            if (prevKey) {
                delete outs[prevKey];
            }

            if (nextKey) {
                if (outs[nextKey]) {
                    throw new Error(`Portal.Out with portalKey="${nextKey}" already exists.`);
                }

                outs[nextKey] = this;
            }

            ins.forEach(cin => cin.reconnect());
        }

        if (this._disconnectedItems.length && this.props.onDisconnect) {
            this.props.onDisconnect(this, this._disconnectedItems);
        }

        if (this._connectedItems.length && this.props.onConnect) {
            this.props.onConnect(this, this._connectedItems);
        }

        this._disconnectedItems = [];
        this._connectedItems = [];
        this._isReconnecting = false;
    }

    componentWillMount() {
        this.componentWillReceiveProps(this.props);
    }

    componentWillReceiveProps(nextProps) {
        if (nextProps.children) {
            throw new Error('Portal.Out must not have own children.');
        }
    }

    componentDidMount() {
        this._reconnect(null, this.props);
    }

    componentDidUpdate(prevProps) {
        this._reconnect(prevProps, this.props);
    }

    componentWillUnmount() {
        this._reconnect(this.props, null);
    }

    render() {
        return <div ref="root" className={`portal out ${this.props.className}`} />;
    }
}

export default class Portal extends React.PureComponent {
    static get In() {
        return In;
    }

    static get Out() {
        return Out;
    }

    constructor() {
        super();
        throw new Error('Portal is not a component. Use Portal.In or Portal.Out.');
    }
};